import { identificadorUnico } from '@/shared/utilidades/identificador'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { NuevaOrdenServicio } from '@/modules/ordenes-servicio/tipos'

import {
  borradoresLocales,
  guardarBorradorLocal,
  marcarBorradorGuardado,
  olvidarBorradorLocal,
} from './borradorLocal'

/**
 * ÓRDENES LEVANTADAS EN LA CALLE QUE ESPERAN SEÑAL.
 *
 * El técnico pasa por una obra, ve algo y lo levanta ahí mismo: es el momento
 * en que más barato sale vender. Pero crear la orden escribe en la base, así
 * que sin cobertura el botón fallaba y se perdía la visita entera —el
 * cliente, la dirección, lo que había que hacer—.
 *
 * Johan: «crear una ODS también debería ser sin servidor de inicio». Así que
 * la orden se guarda aquí tal como la escribió, y cuando vuelve la señal se
 * crea de verdad. Y si ya llevaba renglones cotizados, van detrás: primero la
 * orden, luego su borrador, en ese orden y sin saltarse ninguno.
 */

const BASE = 'mega-ordenes-nuevas'
const ALMACEN = 'pendientes'
const VERSION = 1

export type OrdenEnCola = {
  /** Identificador local. También es la llave del borrador mientras no exista. */
  id: string
  datos: NuevaOrdenServicio
  creado: number
  intentos: number
  ultimoError: string | null
}

let promesaBase: Promise<IDBDatabase> | null = null

function abrir(): Promise<IDBDatabase> {
  if (promesaBase) return promesaBase

  promesaBase = new Promise<IDBDatabase>((resolver, rechazar) => {
    const solicitud = indexedDB.open(BASE, VERSION)

    solicitud.onupgradeneeded = () => {
      const db = solicitud.result
      if (!db.objectStoreNames.contains(ALMACEN)) {
        db.createObjectStore(ALMACEN, { keyPath: 'id' })
      }
    }

    solicitud.onsuccess = () => resolver(solicitud.result)
    solicitud.onerror = () => rechazar(solicitud.error)
    // Sin esto un bloqueo deja la promesa viva para siempre y la pantalla
    // esperando: ya pasó con la cola de marcajes.
    solicitud.onblocked = () => rechazar(new Error('El almacenamiento está ocupado.'))
  })

  promesaBase.catch(() => {
    promesaBase = null
  })
  return promesaBase
}

export async function encolarOrden(datos: NuevaOrdenServicio): Promise<OrdenEnCola> {
  const orden: OrdenEnCola = {
    // El del formulario, si lo trae: así un reintento con señal y esta cola
    // no pueden crear dos órdenes de la misma obra.
    id: datos.id ?? identificadorUnico(),
    datos,
    creado: Date.now(),
    intentos: 0,
    ultimoError: null,
  }

  const db = await abrir()
  await new Promise<void>((resolver, rechazar) => {
    const t = db.transaction(ALMACEN, 'readwrite')
    t.objectStore(ALMACEN).put(orden)
    t.oncomplete = () => resolver()
    t.onerror = () => rechazar(t.error)
  })

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mega:cola-de-ordenes'))
  }

  return orden
}

export async function ordenesPendientes(): Promise<OrdenEnCola[]> {
  const db = await abrir()

  return new Promise((resolver) => {
    const t = db.transaction(ALMACEN, 'readonly')
    const s = t.objectStore(ALMACEN).getAll()
    s.onsuccess = () =>
      resolver(((s.result ?? []) as OrdenEnCola[]).sort((a, b) => a.creado - b.creado))
    s.onerror = () => resolver([])
  })
}

async function guardar(orden: OrdenEnCola) {
  const db = await abrir()
  await new Promise<void>((r) => {
    const t = db.transaction(ALMACEN, 'readwrite')
    t.objectStore(ALMACEN).put(orden)
    t.oncomplete = () => r()
    t.onerror = () => r()
  })
}

async function quitar(id: string) {
  const db = await abrir()
  await new Promise<void>((r) => {
    const t = db.transaction(ALMACEN, 'readwrite')
    t.objectStore(ALMACEN).delete(id)
    t.oncomplete = () => r()
    t.onerror = () => r()
  })
}

/**
 * Sube lo que esperaba: primero las órdenes nuevas, después los borradores.
 *
 * El orden importa. Un borrador cuelga de su orden, así que crear la orden
 * tiene que ir antes; y si la orden no se puede crear, su borrador se queda
 * esperando en vez de perderse.
 */
export async function subirLoQueEspera(): Promise<{ ordenes: number; borradores: number }> {
  let ordenes = 0
  let borradores = 0

  // ── Las órdenes levantadas sin señal ────────────────────────────────────
  for (const orden of await ordenesPendientes()) {
    try {
      /*
       * EL CLIENTE SE ADOPTA AHORA, QUE YA HAY SEÑAL.
       *
       * En la obra se escogió un tercero de World Office pero no se pudo
       * traer a la matriz —eso es una llamada al servidor—. Se hace aquí,
       * antes de crear la orden, porque la orden necesita su `cliente_id`.
       */
      const { wo_id_tercero_pendiente: pendiente, ...datos } = orden.datos

      if (pendiente) {
        const { data: clienteId, error: falloCliente } = await supabase.rpc('fn_adoptar_cliente', {
          p_id_tercero: pendiente,
        })

        if (falloCliente) {
          await guardar({
            ...orden,
            intentos: orden.intentos + 1,
            ultimoError: mensajeDeError(falloCliente),
          })
          continue
        }

        datos.cliente_id = clienteId as string
      }

      /*
       * LA ORDEN NACE CON EL MISMO ID QUE TUVO EN EL TELÉFONO.
       *
       * Antes la base le ponía uno nuevo y solo el borrador se pasaba al id
       * real. Las fotos del paso 3, tomadas sin señal, se quedaban colgando
       * del id del teléfono: al subir no encontraban su orden, la imagen se
       * borraba del almacén y volvía a la cola, para siempre (auditoría sin
       * conexión, oct 2026). Con el mismo id, todo lo que se armó en la obra
       * —fotos, borrador— ya apunta a la orden de verdad.
       *
       * Y el reintento queda a salvo: si la primera vez la orden sí entró
       * pero la respuesta se perdió en la raya de señal, la segunda choca con
       * el id (23505) y se toma la que ya existe en vez de crear otra.
       */
      let { data, error } = await supabase
        .from('ordenes_servicio')
        .insert({ ...datos, id: orden.id } as never)
        .select('id, numero')
        .single()

      if (error?.code === '23505') {
        ;({ data, error } = await supabase
          .from('ordenes_servicio')
          .select('id, numero')
          .eq('id', orden.id)
          .single())
      }

      if (error || !data) {
        await guardar({
          ...orden,
          intentos: orden.intentos + 1,
          ultimoError: error ? mensajeDeError(error) : 'La orden no volvió del servidor.',
        })
        continue
      }

      // El borrador que se armó contra el identificador local pasa a colgar
      // de la orden de verdad.
      // Con el id del teléfono ya no hace falta, salvo para una orden que
      // subió antes de este cambio. Y con el mismo id NO se puede hacer:
      // guardar y olvidar la misma llave borraría el borrador.
      const local = borradoresLocales().find((b) => b.ordenServicioId === orden.id)
      if (local && data.id !== orden.id) {
        guardarBorradorLocal({ ...local, ordenServicioId: data.id })
        olvidarBorradorLocal(orden.id)
      }

      await quitar(orden.id)
      ordenes++
    } catch {
      // Sin red: lo demás tampoco va a salir.
      return { ordenes, borradores }
    }
  }

  // ── Los renglones que se cotizaron sin señal ────────────────────────────
  for (const b of borradoresLocales()) {
    // Lo que el servidor ya tiene no se vuelve a subir.
    if (b.pendiente === false) continue
    // Vacío solo sube si ya había una cotización: es que borró los renglones.
    if (b.lineas.length === 0 && !b.cotizacionId) continue
    // Los que todavía apuntan a una orden local esperan a que su orden suba.
    if (!/^[0-9a-f-]{36}$/i.test(b.ordenServicioId)) continue

    try {
      const { data: cotizacionId, error } = await supabase.rpc('fn_abrir_borrador', {
        p_orden_servicio_id: b.ordenServicioId,
        p_adicional: b.adicional,
      })

      if (error) continue

      const { error: fallo } = await supabase.rpc('fn_guardar_borrador', {
        p_cotizacion_id: cotizacionId as string,
        p_renglones: b.lineas,
      })

      if (fallo) continue

      // Queda la copia, marcada como guardada: sirve para recuperar si el
      // servidor no tiene nada, y no se vuelve a subir.
      marcarBorradorGuardado(b.ordenServicioId)
      borradores++
    } catch {
      return { ordenes, borradores }
    }
  }

  return { ordenes, borradores }
}
